const { arraySum } = require('./utils');

const SCORES = {
	C: 20,
	D: 20,
	H: 30,
	S: 30,
	NT_F: 40, // No-Trump First
	NT: 30,
};

const BONUSES = {
	PART_SCORE: 50,
	INSULT: {
		DOUBLED: 50,
		REDOUBLED: 100,
	},
	GAME: {
		NON_VULNERABLE: 300,
		VULNERABLE: 500,
	},
	SMALL_SLAM: {
		NON_VULNERABLE: 500,
		VULNERABLE: 750,
	},
	GRAND_SLAM: {
		NON_VULNERABLE: 1000,
		VULNERABLE: 1500,
	},
};

const OVERTRICKS = {
	DOUBLED: {
		NON_VULNERABLE: 100,
		VULNERABLE: 200,
	},
	REDOUBLED: {
		NON_VULNERABLE: 200,
		VULNERABLE: 400,
	},
};

const UNDERTRICKS = {
	UNDOUBLED: {
		NON_VULNERABLE: 50,
		VULNERABLE: 100,
	},
	DOUBLED: {
		NON_VULNERABLE: {
			// penalty points for first N undertricks
			// 100 for 1st, 200 for 2nd and 3rd, 300 for any subsequent
			FIRST_N: [100, 200, 200],
			SUBSEQUENT: 300,
		},
		VULNERABLE: {
			FIRST_N: [200],
			SUBSEQUENT: 300,
		},
	},
	REDOUBLED: {
		NON_VULNERABLE: {
			FIRST_N: [200, 400, 400],
			SUBSEQUENT: 600,
		},
		VULNERABLE: {
			FIRST_N: [400],
			SUBSEQUENT: 600,
		},
	},
};

// Amount of points in a minimal contract score to get a Game score bonus.
// If less than this value - then player only gets Part score bonus.
const GAME_SCORE_BONUS_THRESHOLD = 100;

// Amount of tricks to make to get a small / grand slam bonus.
const SMALL_SLAM_TRICKS = 6;
const GRAND_SLAM_TRICKS = 7;

/**
 * @typedef ScoreBoardEntry
 * @property {string} contract.required - Contract result string - eg: "3S XX"
 * @property {string} declarer.required - seat side of the deal's declarer (N, S, W, E) - eg: "N"
 * @property {number} tricks.required - amount of tricks made by a winner team - eg: 9
 * @property {string} winner.required - winner team - eg: "NS"
 * @property {number} points.required - amount of points for a winner team - eg: 380
 */

/** Class representing a Score Board shown in the end of the Game */
class ScoreBoard {
	/**
	 * @param  {Game} game game instance
	 */
	constructor(game) {
		this.game = game;
	}

	/**
	 * Creates a score board entry for the current game.
	 * @returns {ScoreBoardEntry|null}
	 */
	generateEntry() {
		if (!this.game.isComplete) return null;

		let score;

		// if declarer makes the contract then calculate their SCORE
		if (this.game.declaringPair === this.game.winningPair) {
			score = this.calculateTricksScore() + this.calculateBonusScore();
		} else {
			// if the declarer defeats the contract
			// then calculate their PENALTY and add to the opponent's SCORE
			score = this.calculatePenalty();
		}

		return {
			contract: this.contractString(),
			declarer: this.game.declarer.toString(),
			tricks: this.game.madeTricks,
			winner: this.game.winningPair,
			points: score,
		};
	}

	/**
	 * Calculates basic score for the made tricks
	 * @private
	 * @returns {Number}
	 */
	calculateTricksScore() {
		let score = 0;
		let tricks;
		let overtricks = 0;

		if (this.isDoubled || this.isReDoubled) {
			tricks = this.game.contractTricks;
			overtricks = this.game.madeTricks - this.game.minTricks;
		} else {
			tricks = this.game.madeTricks - 6;
		}

		for (let index = 0; index < tricks; index++) {
			score += this.trickScore(this.game.contractSuit(), index === 0);
		}

		if (this.isDoubled) {
			score += overtricks * OVERTRICKS.DOUBLED[this.isVulnerableKey];
		}

		if (this.isReDoubled) {
			score += overtricks * OVERTRICKS.REDOUBLED[this.isVulnerableKey];
		}

		return score;
	}

	/**
	 * Calculates bonus points
	 * @private
	 * @returns {Number}
	 */
	calculateBonusScore() {
		let score = 0;

		if (this.contractScore < GAME_SCORE_BONUS_THRESHOLD) {
			score += BONUSES.PART_SCORE;
		} else {
			score += BONUSES.GAME[this.isVulnerableKey];
		}

		if (this.isDoubled) {
			score += BONUSES.INSULT.DOUBLED;
		}

		if (this.isReDoubled) {
			score += BONUSES.INSULT.REDOUBLED;
		}

		if (this.isSmallSlamContract) {
			score += BONUSES.SMALL_SLAM[this.isVulnerableKey];
		}

		if (this.isGrandSlamContract) {
			score += BONUSES.GRAND_SLAM[this.isVulnerableKey];
		}

		return score;
	}

	/**
	 * Calculates penalty points
	 * @private
	 * @returns {Number}
	 */
	calculatePenalty() {
		let key = 'UNDOUBLED';

		if (this.isDoubled) key = 'DOUBLED';
		if (this.isReDoubled) key = 'REDOUBLED';

		const undertricks = this.game.minTricks - this.game.madeTricks;

		if (key === 'UNDOUBLED') {
			return undertricks * UNDERTRICKS.UNDOUBLED[this.isVulnerableKey];
		}

		const firstNValues = UNDERTRICKS[key][this.isVulnerableKey].FIRST_N;
		const subsequentValue = UNDERTRICKS[key][this.isVulnerableKey].SUBSEQUENT;

		const subsequent =
			undertricks > firstNValues.length ? undertricks - firstNValues.length : 0;

		return (
			arraySum(firstNValues.slice(0, undertricks)) +
			subsequent * subsequentValue
		);
	}

	/**
	 * Calculates the score for a single trick
	 * @private
	 * @param  {String} contractSuit trump
	 * @param  {Boolean} isFirst whether the trick is first in the list
	 * @returns {Number}
	 */
	trickScore(contractSuit, isFirst) {
		let score;

		if (contractSuit === 'NT' && isFirst) {
			score = SCORES.NT_F;
		} else {
			score = SCORES[contractSuit];
		}

		if (this.isDoubled) {
			return score * 2;
		}

		if (this.isReDoubled) {
			return score * 4;
		}

		return score;
	}

	/**
	 * Creates a string with contract info to show in a table. E.g: 7NT XX
	 * @private
	 * @returns {String}
	 */
	contractString() {
		let extra = '';

		if (this.isDoubled) extra += 'X';
		if (this.isReDoubled) extra += 'XX';

		return `${this.game.contract} ${extra}`.trim();
	}

	/**
	 * Calculates the score of a whole contract if a player makes the contract exactly (used to calculate bonuses)
	 * @private
	 * @returns {Number}
	 */
	get contractScore() {
		return this.game.contractSuit() === 'NT'
			? this.trickScore('NT', true) +
					(this.game.contractTricks - 1) * this.trickScore('NT', false)
			: this.game.contractTricks *
					this.trickScore(this.game.contractSuit(), false);
	}

	/**
	 * @private
	 */
	get isSmallSlamContract() {
		return this.game.contractTricks === SMALL_SLAM_TRICKS;
	}

	/**
	 * @private
	 */
	get isGrandSlamContract() {
		return this.game.contractTricks === GRAND_SLAM_TRICKS;
	}

	/**
	 * @private
	 */
	get isVulnerable() {
		return this.game.declarer.isVulnerable(this.game.vulnerable);
	}

	/**
	 * @private
	 */
	get isVulnerableKey() {
		return this.isVulnerable ? 'VULNERABLE' : 'NON_VULNERABLE';
	}

	/**
	 * @private
	 */
	get isDoubled() {
		return this.game.auctionInstance.isDoubled;
	}

	/**
	 * @private
	 */
	get isReDoubled() {
		return this.game.auctionInstance.isReDoubled;
	}
}

module.exports = ScoreBoard;
